/* eslint-disable @typescript-eslint/no-unused-vars */
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import * as moduleState from './state'
import store from 'src/store'
import * as moduleSelectors from './selectors'
import { actions as moduleActions, ContactCreatedPayload } from './actions'
import moduleSagas from './sagas'
import {
  IChangeThreadLabelsSaga,
  IChangeThreadPrivateMode,
  IDeleteCustomLabel,
  IGetConnectedMails,
  IUnlinkEmail,
  IUpdateCustomLabel,
} from 'src/services/mail'
import { builderSagaCommunications } from 'src/helpers/redux'
import {
  IAddressLink,
  ICustomLabel,
  IEmail,
  IMailAccount,
  InternalLabels,
  IThread,
  PrivateValues,
} from './types'
import { IResponse } from 'src/services/mail/http'
import { IPostToggleLabel } from 'src/services/mail/type'

export const emailSlice = createSlice({
  name: moduleState.PREFIX,
  initialState: moduleState.initialState,
  reducers: {
    setNewThread(state, action: PayloadAction<IThread>) {
      if (action.payload) {
        state.data.threads = {
          ...state.data.threads,
          data: [action.payload, ...state.data.threads.data.slice(0, -1)],
        }
      }
    },
    setSentReply(state, action: PayloadAction<IThread>) {
      if (state.data.thread) {
        state.data.thread.lastEmail = action.payload.lastEmail

        state.data.thread.emails = [
          ...(state.data.thread?.emails ?? []),
          action.payload.lastEmail,
        ]
      }
    },
    toggleMailAccountPrivacyMode(state, action: PayloadAction<number>) {
      state.data.partnerMailAccounts = state.data.partnerMailAccounts.map(
        (acc) =>
          acc.id === action.payload
            ? {
                ...acc,
                type:
                  acc.type === PrivateValues.PUBLIC
                    ? PrivateValues.PRIVATE
                    : PrivateValues.PUBLIC,
              }
            : acc
      )
    },
    setUnlinkedMailAccount(state, action: PayloadAction<number>) {
      state.data.partnerMailAccounts = state.data.partnerMailAccounts.map(
        (acc) =>
          acc.id === action.payload ? { ...acc, status: 'deactivate' } : acc
      )

      state.data.userMailAccounts = state.data.userMailAccounts.map((acc) =>
        acc.id === action.payload ? { ...acc, status: 'deactivate' } : acc
      )
    },
    setPartnerMailAccount(state, action: PayloadAction<IMailAccount>) {
      state.data.partnerMailAccounts = state.data.partnerMailAccounts.find(
        (acc) => acc.identifier === action.payload.identifier
      )
        ? state.data.partnerMailAccounts.map((acc) =>
            acc.identifier === action.payload.identifier ? action.payload : acc
          )
        : [...state.data.partnerMailAccounts, action.payload]

      state.data.userMailAccounts = state.data.userMailAccounts.find(
        (acc) => acc.identifier === action.payload.identifier
      )
        ? state.data.userMailAccounts.map((acc) =>
            acc.identifier === action.payload.identifier ? action.payload : acc
          )
        : [...state.data.userMailAccounts, action.payload]
    },
    setThreads(state, action: PayloadAction<IResponse<IThread[]>>) {
      state.data.threads = {
        meta: {
          currentPage: action.payload.meta.currentPage,
          lastPage: action.payload.meta.lastPage,
        },
        data: action.payload.data,
      }
    },

    setUserMailAccounts(state, action: PayloadAction<IMailAccount[]>) {
      state.data.userMailAccounts = action.payload
    },
    setPartnerMailAccounts(state, action: PayloadAction<IMailAccount[]>) {
      state.data.partnerMailAccounts = action.payload
    },

    setCustomLabels(
      state,
      action: PayloadAction<moduleState.IState['data']['customLabels']>
    ) {
      state.data.customLabels = action.payload
    },

    mergeLabels(state, action: PayloadAction<IResponse<ICustomLabel[]>>) {
      state.data.customLabels = {
        data: [...(state.data.customLabels.data ?? []), ...action.payload.data],
        nextCursor: action.payload.meta.nextCursor,
      }
    },

    setToggledLabelThreads(
      state,
      action: PayloadAction<IPostToggleLabel & { isInternal?: boolean }>
    ) {
      const label = state.data.customLabels.data.find(
        (label) => label.code === action.payload.labelsCodes
      )

      const isThreadSelected = action.payload.threadsIds.find(
        (threadId) => threadId === state.data.thread?.id
      )

      if (isThreadSelected && label && state.data.thread) {
        if (
          action.payload.labelsCodes === InternalLabels.TRASH &&
          state.data.thread.labels.find(
            (label) => label.code === InternalLabels.TRASH
          )
        ) {
          state.data.thread = null
        } else {
          state.data.thread.labels =
            action.payload.operation === 'add'
              ? [...(state.data.thread?.labels ?? []), label]
              : state.data.thread.labels.filter((l) => l.code != label.code)
        }
      }

      state.data.threads.data = action.payload.isInternal
        ? state.data.threads.data.filter(
            (thread) =>
              !action.payload.threadsIds.find((t) => {
                return t === thread.id
              })
          )
        : state.data.threads.data.map((t) => ({
            ...t,
            labels:
              action.payload.threadsIds.find((id) => id === t.id) && label
                ? action.payload.operation === 'add'
                  ? [...t.labels, label]
                  : t.labels.filter((l) => l.code != label.code)
                : t.labels,
          }))
    },

    setUpdatedLabel(state, action: PayloadAction<IUpdateCustomLabel>) {
      state.data.customLabels = {
        ...state.data.customLabels,
        data: (state.data.customLabels.data ?? []).map((i) => {
          if (i.code === action.payload.code) {
            return { ...i, ...action.payload }
          }

          return i
        }),
      }
    },

    setDeletedLabel(state, action: PayloadAction<IDeleteCustomLabel>) {
      state.data.customLabels = {
        ...state.data.customLabels,
        data: (state.data.customLabels.data ?? []).filter(
          (i) => i.code != action.payload.label
        ),
      }
    },

    setCreatedLabel(state, action: PayloadAction<ICustomLabel>) {
      state.data.customLabels = {
        ...state.data.customLabels,
        data: [...(state.data.customLabels?.data ?? []), action.payload],
      }
    },

    changeThreadPrivacyMode(
      state,
      action: PayloadAction<IChangeThreadPrivateMode>
    ) {
      state.data.threads.data = state.data.threads.data.map((i) => {
        if (i.identifier === action.payload.threadId) {
          return { ...i, shared: action.payload.privacyMode === 'share' }
        } else {
          return i
        }
      })
    },

    setThreadMessages(state, action: PayloadAction<IThread>) {
      state.data.thread = action.payload
    },

    setAssignLink(
      state,
      action: PayloadAction<moduleState.IState['data']['assignLink']>
    ) {
      state.data.assignLink = action.payload
    },
    setEmailToThread(state, action: PayloadAction<IEmail>) {
      if (state.data.thread) {
        state.data.thread.emails = [
          ...(state.data.thread?.emails ?? []),
          action.payload,
        ]
      }
    },
  },
  extraReducers: (builder) => {
    builderSagaCommunications<moduleState.IState>(builder, moduleActions)
  },
})

export const reducers = { [moduleState.PREFIX]: emailSlice.reducer }
export const selectors = moduleSelectors
export const setters = emailSlice.actions
export const actions = moduleActions
export const sagas = moduleSagas

export const injectStore = () => {
  store.injectReducer?.(moduleState.PREFIX, emailSlice.reducer)
  store.injectSaga?.(moduleState.PREFIX, sagas)
}
